import { Timestamp } from 'firebase/firestore';
import moment from 'moment';

/**
 * Universal Date Handling Utility
 * 
 * Handles all possible date formats that may exist in legacy data:
 * - Firestore Timestamps
 * - JavaScript Date objects  
 * - ISO date strings
 * - Unix timestamps (milliseconds)
 * - Unix timestamps (seconds)
 * - Moment objects
 * - null/undefined values
 */

/**
 * Convert any date format to a JavaScript Date object
 * @param {*} dateValue - Any date format
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const toDate = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // Already a Date object
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    
    // Firestore Timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // Moment object
    if (moment.isMoment(dateValue)) {
      return dateValue.toDate();
    }
    
    // Unix timestamp (milliseconds) - numbers > 1000000000000 (year 2001)
    if (typeof dateValue === 'number' && dateValue > 1000000000000) {
      return new Date(dateValue);
    }
    
    // Unix timestamp (seconds) - numbers between 1000000000 (2001) and 10000000000 (2286)
    if (typeof dateValue === 'number' && dateValue > 1000000000 && dateValue < 10000000000) {
      return new Date(dateValue * 1000);
    }
    
    // String values
    if (typeof dateValue === 'string') {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    // Object with seconds/nanoseconds (legacy Firestore format)
    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      return new Date(dateValue.seconds * 1000 + (dateValue.nanoseconds || 0) / 1000000);
    }
    
    console.warn('Unrecognized date format:', dateValue);
    return null;
    
  } catch (error) {
    console.warn('Error converting date:', dateValue, error);
    return null;
  }
};

/**
 * Convert any date format to a Firestore Timestamp
 * @param {*} dateValue - Any date format
 * @returns {Timestamp|null} - Firestore Timestamp or null if invalid
 */
export const toTimestamp = (dateValue) => {
  const date = toDate(dateValue);
  return date ? Timestamp.fromDate(date) : null;
};

/**
 * Convert any date format to a moment object
 * @param {*} dateValue - Any date format
 * @returns {moment.Moment|null} - Moment object or null if invalid
 */
export const toMoment = (dateValue) => {
  const date = toDate(dateValue);
  return date ? moment(date) : null;
};

/**
 * Format date for display with fallback handling
 * @param {*} dateValue - Any date format
 * @param {string} format - Moment format string (default: 'DD/MM/YYYY HH:mm')
 * @returns {string} - Formatted date or 'Not set' if invalid
 */
export const formatDate = (dateValue, format = 'DD/MM/YYYY HH:mm') => {
  const momentObj = toMoment(dateValue);
  return momentObj ? momentObj.format(format) : 'Not set';
};

/**
 * Check if a date value is valid
 * @param {*} dateValue - Any date format
 * @returns {boolean} - True if valid date
 */
export const isValidDate = (dateValue) => {
  return toDate(dateValue) !== null;
};

/**
 * Get time remaining until a date
 * @param {*} dateValue - Any date format  
 * @returns {object} - Object with remaining time details
 */
export const getTimeRemaining = (dateValue) => {
  const date = toDate(dateValue);
  if (!date) return { isOverdue: false, timeText: 'No date set' };
  
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  
  if (diff < 0) {
    const overdueMoment = moment.duration(Math.abs(diff));
    return {
      isOverdue: true,
      timeText: `${overdueMoment.humanize()} overdue`,
      totalMinutes: Math.floor(Math.abs(diff) / (1000 * 60))
    };
  }
  
  const remainingMoment = moment.duration(diff);
  return {
    isOverdue: false,
    timeText: `${remainingMoment.humanize()} remaining`,
    totalMinutes: Math.floor(diff / (1000 * 60))
  };
};

/**
 * Standardize date field in an object
 * @param {object} obj - Object containing date fields
 * @param {string} fieldName - Name of the date field
 * @returns {object} - Object with standardized date field
 */
export const standardizeDateField = (obj, fieldName) => {
  if (!obj || !obj[fieldName]) return obj;
  
  const standardizedDate = toTimestamp(obj[fieldName]);
  return {
    ...obj,
    [fieldName]: standardizedDate
  };
};

/**
 * Batch standardize multiple date fields in an object
 * @param {object} obj - Object containing date fields
 * @param {string[]} fieldNames - Array of date field names
 * @returns {object} - Object with all date fields standardized
 */
export const standardizeDateFields = (obj, fieldNames = []) => {
  if (!obj) return obj;
  
  let result = { ...obj };
  for (const fieldName of fieldNames) {
    result = standardizeDateField(result, fieldName);
  }
  
  return result;
};

/**
 * Legacy data migration utility
 * Converts old date formats to Firestore Timestamps
 * @param {array} items - Array of objects with date fields
 * @param {string[]} dateFields - Array of date field names to convert
 * @returns {array} - Array with standardized dates
 */
export const migrateLegacyDates = (items, dateFields = ['dueDate', 'createdAt', 'updatedAt']) => {
  if (!Array.isArray(items)) return items;
  
  return items.map(item => standardizeDateFields(item, dateFields));
};

/**
 * Safe date comparison
 * @param {*} date1 - First date (any format)
 * @param {*} date2 - Second date (any format)
 * @returns {number} - -1 if date1 < date2, 0 if equal, 1 if date1 > date2, null if either is invalid
 */
export const compareDates = (date1, date2) => {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  
  if (!d1 || !d2) return null;
  
  const time1 = d1.getTime();
  const time2 = d2.getTime();
  
  if (time1 < time2) return -1;
  if (time1 > time2) return 1;
  return 0;
};

/**
 * Check if a date is overdue
 * @param {*} dateValue - Any date format
 * @param {*} referenceDate - Reference date (default: now)
 * @returns {boolean} - True if overdue
 */
export const isOverdue = (dateValue, referenceDate = new Date()) => {
  const date = toDate(dateValue);
  const refDate = toDate(referenceDate);
  
  if (!date || !refDate) return false;
  
  return date.getTime() < refDate.getTime();
};

/**
 * Get the start of day for a date
 * @param {*} dateValue - Any date format
 * @returns {Date|null} - Start of day or null if invalid
 */
export const startOfDay = (dateValue) => {
  const date = toDate(dateValue);
  if (!date) return null;
  
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of day for a date
 * @param {*} dateValue - Any date format
 * @returns {Date|null} - End of day or null if invalid
 */
export const endOfDay = (dateValue) => {
  const date = toDate(dateValue);
  if (!date) return null;
  
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export default {
  toDate,
  toTimestamp,
  toMoment,
  formatDate,
  isValidDate,
  getTimeRemaining,
  standardizeDateField,
  standardizeDateFields,
  migrateLegacyDates,
  compareDates,
  isOverdue,
  startOfDay,
  endOfDay
}; 